import { ThemeEvents } from '@theme/events';
import { morph } from '@theme/morph';
import { Component } from '@theme/component';

/**
 * In the quick-add modal, delivery is informational only (no drawer). Server HTML
 * always renders a <button>; call this after morph or on modal open.
 * @param {ParentNode} root
 */
export function staticifyDeliveryRows(root) {
  for (const row of root.querySelectorAll('[data-delivery-row]')) {
    if (!(row instanceof HTMLButtonElement)) continue;

    const status = document.createElement('div');
    status.className = row.className;
    status.setAttribute('role', 'status');
    status.setAttribute('data-delivery-row', '');
    status.setAttribute('data-delivery-row-static', '');
    for (const [key, value] of Object.entries(row.dataset)) {
      status.dataset[key] = value;
    }
    status.append(...row.childNodes);
    // Modal delivery is informational — no drawer affordance.
    for (const chevron of status.querySelectorAll('.dev-product-card-delivery__chevron')) {
      chevron.remove();
    }
    row.replaceWith(status);
  }
}

/**
 * Quick-add modal only needs the delivery row under ATC — remove drawer markup
 * so duplicate IDs and scripts do not land in the modal DOM.
 * @param {ParentNode} root
 */
export function stripDeliveryDrawerFromModal(root) {
  const wrapper = root.querySelector('[data-delivery-wrapper]');
  if (!wrapper) return;

  wrapper.querySelector('#dev-delivery-info-drawer')?.remove();

  for (const script of wrapper.querySelectorAll('script[src*="dev-delivery-info"], script[src*="dev-drawer"]')) {
    script.remove();
  }
}

/**
 * dev-delivery-info — Custom Element for the delivery info row on PDP.
 *
 * Piggybacks on Horizon's variant:update event (same pattern as product-inventory.js).
 * When a variant changes, Horizon fetches the full updated page HTML once and dispatches
 * the event. We read our element from that cached HTML and morph our DOM — zero extra fetches.
 *
 * The Liquid in blocks/dev-product-card-delivery.liquid handles all business logic
 * (stock status, delivery days, back-order date calculations). This file only manages DOM.
 */
class DevDeliveryInfo extends Component {
  connectedCallback() {
    super.connectedCallback();
    this.closest('.shopify-section, dialog')
      ?.addEventListener(ThemeEvents.variantUpdate, this.updateDelivery);
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    this.closest('.shopify-section, dialog')
      ?.removeEventListener(ThemeEvents.variantUpdate, this.updateDelivery);
  }

  /**
   * @param {import('@theme/events').VariantUpdateEvent} event
   */
  updateDelivery = (event) => {
    // Custom bundle PDP: delivery is driven by configurator (max of components), not parent variant morph
    if (this.hasAttribute('data-dev-bundle-delivery')) return;

    if (event.detail.data.newProduct) {
      this.dataset.productId = event.detail.data.newProduct.id;
    } else if (
      event.target instanceof HTMLElement &&
      event.target.dataset.productId !== this.dataset.productId
    ) {
      return;
    }

    const productId = this.dataset.productId;
    const fresh = productId
      ? event.detail.data.html.querySelector(`dev-delivery-info[data-product-id="${productId}"]`)
      : event.detail.data.html.querySelector('dev-delivery-info');
    if (!fresh) return;

    morph(this, fresh, { childrenOnly: true });

    const wrapper = this.closest('[data-delivery-wrapper]');
    const freshWrapper = fresh.closest('[data-delivery-wrapper]');
    const freshDrawerSections = freshWrapper?.querySelector('[data-dev-delivery-drawer-sections]');
    const drawerSections = wrapper?.querySelector('[data-dev-delivery-drawer-sections]');
    if (freshDrawerSections && drawerSections) {
      morph(drawerSections, freshDrawerSections, { childrenOnly: true });
    }

    window.NoomaDeliveryI18n?.paint?.(this.closest('[data-delivery-wrapper]') ?? this);

    if (this.closest('.quick-add-modal')) {
      staticifyDeliveryRows(this);
      stripDeliveryDrawerFromModal(this.closest('.quick-add-modal') ?? this);
    }
  };
}

if (!customElements.get('dev-delivery-info')) {
  customElements.define('dev-delivery-info', DevDeliveryInfo);
}
